import React, { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Button,
  InputNumber,
  Space,
  Checkbox,
  Typography,
  Select,
  Spin,
} from 'antd';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';
import map from 'lodash/map';
import filter from 'lodash/filter';
import sumBy from 'lodash/sumBy';
import assign from 'lodash/assign';
import includes from 'lodash/includes';
import { ArrowLeftOutlined } from '@ant-design/icons';
import MediaUploadModal from '../../../components/product/mediaUploadModal';
import { orderManagement } from '../../../redux/actions/manageOrderActions';

const { Text } = Typography;
const { Option } = Select;

const OfferingDetails = (props) => {
  const { edit, initialValues, onMove, onSubmitPayload, selectProducts } =
    props;
  const [loading, setLoading] = useState(false);
  const [showRetailer, setShowRetailer] = useState(
    initialValues && get(initialValues, 'availability') === 'SELECTIVE'
  );
  const [partnerRetailers, setPartnerRetailers] = useState([]);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [media, setMedia] = useState(
    initialValues ? get(initialValues, 'media', []) : []
  );

  const onFinish = (values) => {
    onSubmitPayload(
      2,
      assign(values, {
        media,
        partnerRetailers: filter(partnerRetailers, (retailer) =>
          includes(get(values, 'partner_retailer'), get(retailer, 'id'))
        ),
      })
    );
  };

  const onCheckItems = (checked) => {
    form.setFieldsValue({
      item_count: checked ? selectProducts.length : undefined,
    });
  };

  const onCheckMRP = (checked) => {
    form.setFieldsValue({
      mrp: checked ? sumBy(selectProducts, 'mrp') : undefined,
    });
  };

  const fetchPartnerRetailers = () => {
    setLoading(true);
    dispatch(orderManagement('list.partner_retailers'))
      .then((result) => {
        setPartnerRetailers(get(result, 'data'));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPartnerRetailers();
  }, []);

  return (
    <Spin spinning={loading}>
      <Form
        name="basic"
        layout="vertical"
        form={form}
        initialValues={initialValues || {}}
        onFinish={onFinish}
        autoComplete="off"
        requiredMark
      >
        <Row gutter={32} className="mt-60">
          <Col xl={6}>
            <Form.Item
              label="Name of Offering"
              name="name"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please input Offer name!',
                },
              ]}
            >
              <Input size="large" placeholder="Enter Offering name" />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="MRP of Offering"
              name="mrp"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please input Offering MRP!',
                },
              ]}
              extra={
                <Space size="small" className="mt-10">
                  <Checkbox
                    onChange={(e) => {
                      onCheckMRP(get(e, 'target.checked'));
                    }}
                  />
                  <Text type="secondary">Same as MRP</Text>
                </Space>
              }
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Eg. 100"
              />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Number of Items of Offering"
              name="item_count"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please input Number of items!',
                },
              ]}
              extra={
                <Space size="small" className="mt-10">
                  <Checkbox
                    onChange={(e) => {
                      onCheckItems(get(e, 'target.checked'));
                    }}
                  />
                  <Text type="secondary">Same as number of items</Text>
                </Space>
              }
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Eg.4"
              />
            </Form.Item>
          </Col>

          <Col xl={6}>
            <Form.Item
              label="Discount (%)"
              name="discount_percent"
              className="form-item"
              rules={[
                {
                  type: 'number',
                  max: 100,
                  message: 'Max discount is 100% !',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="10"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="mt-20" gutter={32}>
          <Col xl={6}>
            <Form.Item
              label="Availability"
              name="availability"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select availability type!',
                },
              ]}
            >
              <Select
                size="large"
                disabled={edit}
                placeholder="Availability"
                onChange={(value) => {
                  setShowRetailer(value === 'SELECTIVE');
                }}
                options={[
                  {
                    value: 'SELECTIVE',
                    label: 'Selectively',
                  },
                  {
                    value: 'PUBLIC',
                    label: 'Public',
                  },
                ]}
              />
            </Form.Item>
          </Col>
          {showRetailer && (
            <Col xl={6}>
              <Form.Item
                label="Retailers"
                name="partner_retailer"
                className="form-item"
                rules={[
                  {
                    required: true,
                    message: 'Please select retailers!',
                  },
                ]}
              >
                <Select
                  size="large"
                  disabled={edit}
                  placeholder="Select Retailers"
                  showSearch
                  mode="multiple"
                  filterOption={(input, option) => {
                    return (
                      get(option, 'children').toLowerCase() ?? ''
                    ).includes(input.toLowerCase());
                  }}
                >
                  {map(partnerRetailers, (retailer) => (
                    <Option
                      value={get(retailer, 'id')}
                      key={get(retailer, 'id')}
                    >
                      {get(retailer, 'name')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row className="mt-60">
          <Col xl={6}>
            <MediaUploadModal media={media} setMedia={setMedia} />
          </Col>
        </Row>
        <Row justify="space-evenly" className="mt-60">
          <Col xl={8}>
            <Button
              className="default-primary-btn"
              type="primary"
              onClick={() => onMove(1)}
              icon={<ArrowLeftOutlined />}
            >
              Back
            </Button>
          </Col>
          <Col xl={12} />
          <Col xl={4}>
            <Button
              htmlType="submit"
              className="default-primary-btn"
              type="primary"
              block
            >
              Save & Proceed
            </Button>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default OfferingDetails;
